import ImageEditor from "@Components/ImageEditor";
import GoogleMap from "@Components/Map";
import { optionsViewType } from "@Features/portal-settings";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DateTimeComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getDistanceInMeters, DEFAULT_IMAGE_DISPLAY_WIDTH } from "@Helpers/constants";
import { t } from "@i18n";
import { Box, CircularProgress, FormControlLabel, FormGroup, Link, RadioGroup } from "@mui/material";
import { convertDate } from "@Services/luxonHelper";
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import { getBackendUrl } from "@Utils";
import _ from "lodash";
import { formSectionType, getUrlFromField } from "./constants";
import { getCalculatedFieldHintText, getConcatenateFieldValue, getFormulaFieldValue } from "./useQuestionFields";
import UserCard from "./userCard";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import Text from "@CubeComponents/html/Text/Text";
import { getViewerUrl } from "@Helpers/urlHelpers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import type { ReactNode } from "react";
import type { TreeDropDownNode } from "@CubeComponents/html/TreeDropDown";

/**
 * A question as this renderer sees it — the stored/wire shape from `questionFieldSchema`, which
 * already models `displaySize` and the full `displayAs` set (including `dateAndTime`, which the
 * older hand-written type was missing).
 */
export type RenderableQuestionField = QuestionField & {
  /**
   * Persisted by the form-template builder (portal-settings FormsAndFields/FormTemplate) and read
   * by both the web and mobile renderers, but NOT modelled by `questionFieldSchema` — its
   * `.catchall(z.unknown())` would type them `unknown`, which no JSX position accepts. Declared
   * here, and reported as a server-schema gap; delete these two once the schema carries them.
   */
  requireAttachments?: boolean;
  fieldInstruction?: string;

  // KNOWN BUG, preserved verbatim: every `p.eachField.defaultValue` read below is dead. Nothing
  // writes `defaultValue` onto a question field — the only `defaultValue` in the form-template
  // builder (portal-settings FormsAndFields/FormTemplate/constants.tsx) is a per-property EDITOR
  // default ("text", "newTab") consumed by its own sidebar, never persisted onto the question — and
  // `questionFieldSchema` does not model it either. So the widgets' "template preset answer" always
  // falls through to `currentAnswer.value`. Typed `undefined` because that is what it always is; the
  // fix is to add `defaultValue` to `questionFieldSchema` and have the builder write it, at which
  // point this alias comes off and the reads become live.
  defaultValue?: undefined;
};

/* -------------------------------------------------------------------------- */
/* Answer value shapes, one per `fieldType`.                                   */
/*                                                                             */
/* `answerSchema.value` is `z.unknown()` on the server because the runtime      */
/* shape is chosen by the question's `fieldType`. Rather than leave that as     */
/* `any`, each shape is named here and each renderer declares the one it reads  */
/* (see `QuestionFieldRenderProps<TValue>`), so the renderer bodies need no     */
/* casts at all and a wrong read is a compile error.                           */
/* -------------------------------------------------------------------------- */

/**
 * A file / image entry stored in a `fileUpload` or `image` answer. `awsKey` is present for uploads
 * that went to S3 (the attachment endpoint streams them back by key); `fileVersion` for entries
 * picked out of the File Manager instead, which open through the viewer.
 */
export type FieldFileValue = {
  awsKey?: string;
  name?: string;
  type?: string;
  fileVersion?: string;
  fileId?: string;
  resourceType?: string;
  versionNumber?: number;
};

/** A user entry stored in a `users` answer; `_id` is the account id the user card is keyed by. */
export type FieldUserValue = { _id: string } & Partial<UserTagProfileData>;

/** An entry stored in a `look-up` answer — a project Location node or a Team. */
export type FieldLookUpValue = { id: string; name: string };

/** The value stored by a `url` answer. */
export type FieldUrlValue = { URL?: string; displayText?: string };

/** A geo point as the answer stores it and `GoogleMap` consumes it. */
export type FieldGeoPoint = { lat: number; lng: number };

/** The value stored by a `geoLocation` answer. */
export type FieldGeoValue = { current?: FieldGeoPoint; selected?: FieldGeoPoint; mapZoomLevel?: number };

/** Every answer shape the renderers below handle, as a single union for the read-only view. */
export type FieldAnswerValue =
  | string
  | number
  | string[]
  | FieldFileValue[]
  | FieldUserValue[]
  | FieldLookUpValue[]
  | FieldUrlValue
  | FieldGeoValue
  | null;

/**
 * The captured answer for one question. `createdBy` / `createdDate` come from the wire schema;
 * `value` is parameterised because its runtime shape is chosen by `eachField.fieldType`.
 */
export type FieldAnswer<TValue = unknown> = NonNullable<QuestionField["answer"]> & { value?: TValue };

/**
 * The subset of `useFormValidator`'s return value the renderers read. Declared structurally so the
 * redux-held validator and a locally created one are both accepted.
 */
type FieldFormValidator = {
  error?: Record<string, string>;
  validate?: (args: { name: string; value: unknown }) => boolean;
};

/**
 * The form instance as the renderers forward it; only `_id` and the template are ever read. The
 * sections are `Partial` so both a live instance (whose `sectionHeading` is optional on the wire)
 * and a strict authored template satisfy it.
 */
type RendererFormData = { _id?: string; selectedForm?: { template?: Array<Partial<FormTemplateField>> } };

/**
 * Payload handed back to `useForms`' `removeUserFromList`. Mirrors the interface of the same name
 * in userCard.tsx (file-local there, so it cannot be imported), widened where this call site may
 * legitimately have nothing to send — the read-only answer view builds its props by hand.
 */
export type RemoveUserFromListArgs = {
  fieldIndex: number | null | undefined;
  sectionIndex: number | null | undefined;
  user: Partial<UserTagProfileData>;
  section: Partial<FormTemplateField> | undefined;
  eachField: QuestionField;
  source: string | undefined;
  tableRowIndex: number | undefined;
  /** Forwarded untouched to the hook, which decides what to do with them. */
  handleCustomFieldData: unknown;
  extraParams: unknown;
};

/** Payload for the "add users to this question" modal opener. */
export type OpenAddUserModalArgs = {
  eachField: RenderableQuestionField;
  fieldIndex: number | null;
  sectionIndex: number | null;
  section: Partial<FormTemplateField>;
  source: string;
  tableRowIndex: number;
  handleCustomFieldData: unknown;
  extraParams: unknown;
  tableRowId: string | number | null;
};

/** Payload for a local-device file/image pick on a `fileUpload` / `image` question. */
export type FileUploadChangeArgs = {
  e: React.ChangeEvent<HTMLInputElement>;
  sectionId: string;
  eachField: RenderableQuestionField;
  sectionIndex: number | null;
  fieldIndex: number | null;
  source: string;
  tableRowIndex: number;
  tableRowId: string | number | null;
  allowedFileTypes: QuestionField["allowedFileTypes"];
  formData: RendererFormData;
  handleCustomFieldData: unknown;
  extraParams: unknown;
};

/** Payload for removing one already-uploaded file/image from an answer. */
export type RemoveUploadedFileArgs = {
  fileData: FieldFileValue;
  eachField: RenderableQuestionField;
  sectionIndex: number | null;
  fieldIndex: number | null;
  sectionId: string;
  questionId: string;
  tableRowIndex: number;
  source: string;
  handleCustomFieldData: unknown;
  currentAnswer: FieldAnswer<FieldFileValue[]>;
  /** Absent on the image-editor delete path, which never sent it. */
  extraParams?: unknown;
};

/**
 * Payload for opening the URL / geo-location editor modals. `state` toggles visibility and is the
 * only required member: the renderers below open a modal with the full payload, but the modals
 * themselves close again by calling the same handler with a bare `{ state: false }`.
 */
export type FieldModalStateArgs = {
  state: boolean;
  sectionIndex?: number | null;
  eachField?: RenderableQuestionField;
  tableRowIndex?: number;
  answer?: FieldAnswer<unknown>;
  fieldIndex?: number | null;
  sectionId?: string;
  source?: string;
  tableRowId?: string | number | null;
};

/** Payload for persisting an image edited in place. */
export type SaveEditedImageArgs = {
  eachField: RenderableQuestionField;
  fieldIndex: number | null;
  sectionIndex: number | null;
  tableRowIndex: number;
  source: string;
  imageIndex: number;
  base64EncodedImg: string;
  handleCustomFieldData: unknown;
  currentAnswer: FieldAnswer<FieldFileValue[]>;
  extraParams: unknown;
  awsKey: string | undefined;
  sectionId: string;
  questionId: string;
};

export type QuestionFieldRenderProps<TValue = unknown> = {
  eachField: RenderableQuestionField;
  currentAnswer: FieldAnswer<TValue>;
  enabled: boolean;
  name: string;
  source: string;
  width?: string;
  formValidator: FieldFormValidator;
  section: Partial<FormTemplateField>;
  fieldIndex: number | null;
  sectionIndex: number | null;
  sectionId: string;
  questionId: string;
  tableRowIndex: number;
  tableRowId: string | number | null;
  formData: RendererFormData;
  extraParams: Record<string, unknown>;
  spaceId?: string;
  projectId?: string;
  multiTextRows?: number;
  formulaValue?: number | null;
  concatenatedValue?: string | null;
  locationData: Array<{ id: string; label: string; parentId?: string | null }>;
  teamsData: Array<{ id: string; name: string }>;
  isFileUploading: Array<{ type?: string; uniqueId: string | number }>;
  /** Forwarded untouched to `useForms`; this file never invokes it. */
  handleCustomFieldData: unknown;
  handleOnChange: (args?: unknown) => void;
  handleOnBlur: (args?: unknown) => void;
  handleRemoveLocation: () => void;
  handleFileUploadClick: () => void;
  handleUrlFieldClick: () => void;
  handleFileUploadChange: (args: FileUploadChangeArgs) => void;
  handleRemoveUploadedFile: (args: RemoveUploadedFileArgs) => void;
  handleURLModalState: (args: FieldModalStateArgs) => void;
  handleGeoLocationModalState: (args: FieldModalStateArgs) => void;
  removeUserFromList: (args: RemoveUserFromListArgs) => void;
  openAddUserModal: (args: OpenAddUserModalArgs) => void;
  saveEditedImage: (args: SaveEditedImageArgs) => void;
};

/**
 * What the read-only answer view (`getFieldAns`) is given. Everything is optional because
 * `formToPrint` builds this object by hand out of five fields — none of the live renderer's
 * handlers exist on that path.
 */
export type FieldAnswerViewProps = Partial<QuestionFieldRenderProps<unknown>> & {
  eachField: RenderableQuestionField;
};

export const getMapWarnings = (warningMessage = "") => (
  <div className={`h-12 bg-system_colour_12 w-fit ps-3 gap-2 bg-opacity-30 pe-3 rounded-lg flex items-center`}>
    <span
      className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default bg-yellow-400`}
    >
      !
    </span>
    <span>{warningMessage}</span>
  </div>
);

export const getOverDistanceWarning = () => getMapWarnings(t("Selected location differs from captured location"));

export const getNoPermissionWarning = () =>
  getMapWarnings(t("Live location not captured since necessary permission not given"));

export const getUserCardAndChip = ({ user, p }: { user: FieldUserValue; p: FieldAnswerViewProps }) => {
  return p.eachField.viewType === optionsViewType.CHIP ? (
    <UserNameTagComponent
      id={user._id}
      // `removeUserFromList!` below is erased at emit. It is absent only on the read-only print
      // path, which always arrives with `enabled: false`, so this arrow is not built there.
      // NOTE: this comment sits above the attribute, not inside the ternary. Placed after the `?`,
      // it makes esbuild parenthesise the arrow, and the emitted JS then differs from HEAD by two
      // bytes — harmless, but it costs the emitted-JS comparison its clean zero.
      handleDeleteIconClicked={
        (p.enabled
          ? () =>
              p.removeUserFromList!({
                fieldIndex: p.fieldIndex,
                sectionIndex: p.sectionIndex,
                user,
                eachField: p.eachField,
                section: p.section,
                source: p.source,
                tableRowIndex: p.tableRowIndex,
                handleCustomFieldData: p.handleCustomFieldData,
                extraParams: p.extraParams,
              })
          : null) as (() => void) | undefined
      }
    />
  ) : (
    <UserCard
      fieldIndex={p.fieldIndex}
      sectionIndex={p.sectionIndex}
      section={p.section}
      source={p.source}
      tableRowIndex={p.tableRowIndex}
      showAnswersOnly={!p.enabled}
      handleCustomFieldData={p.handleCustomFieldData}
      extraParams={p.extraParams}
      removeUserFromList={p.removeUserFromList}
      field={p.eachField}
      id={user._id}
    />
  );
};

export const renderSingleLine = (p: QuestionFieldRenderProps<string>) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? null}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    disabled={!p.enabled}
    size="small"
    key={p.name}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
  />
);

export const renderMultiLine = (p: QuestionFieldRenderProps<string>) => (
  <textarea
    style={{
      border: "1px solid #19204880",
      outline: "none",
      width: p.source === formSectionType.FORM ? "80%" : "100%",
      padding: "0.42rem 0 0.42rem 0.5rem",
      height: "100%",
      color: "black",
    }}
    disabled={!p.enabled}
    rows={p.multiTextRows || 5}
    key={p.name}
    name={p.name}
    placeholder={p.eachField.placeholderText ?? ""}
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? ""}
    onBlur={p.handleOnBlur}
    onChange={p.handleOnChange}
  />
);

export const renderSingleSelect = (p: QuestionFieldRenderProps<string>) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <RadioGroup
        value={p.currentAnswer.value ?? undefined}
        defaultValue={p.eachField.defaultValue}
        onChange={(args) => {
          p.handleOnBlur(args);
          p.handleOnChange(args);
        }}
      >
        {p.eachField.answerChoices?.map((choice: string) => (
          <FormControlLabel
            key={choice}
            disabled={!p.enabled}
            value={choice}
            control={<RadioButton />}
            label={choice}
          />
        ))}
      </RadioGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      getOptionLabel={(opt) => opt}
      sx={{ width: p.width }}
      renderInput={(params) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

export const renderMultiSelect = (p: QuestionFieldRenderProps<string[]>) => {
  if (p.eachField.viewType === optionsViewType.LIST) {
    return (
      <FormGroup>
        {p.eachField.answerChoices?.map((choice: string) => (
          <FormControlLabel
            key={choice}
            control={
              <CheckBoxComponent
                onChange={(args) => {
                  p.handleOnBlur(args);
                  p.handleOnChange(args);
                }}
                disabled={!p.enabled}
                name={choice}
                checked={Array.isArray(p.currentAnswer.value) && p.currentAnswer.value.includes(choice)}
              />
            }
            label={choice}
          />
        ))}
      </FormGroup>
    );
  }

  return (
    <AutoComplete
      disabled={!p.enabled}
      multiple
      key={p.name}
      disableCloseOnSelect
      value={p.currentAnswer.value ?? undefined}
      defaultValue={p.eachField.defaultValue ?? []}
      getOptionLabel={(opt) => opt}
      onChange={(_, newValue) => {
        p.handleOnChange({ target: { value: newValue } });
        p.handleOnBlur({ target: { value: newValue } });
      }}
      id={p.name}
      options={p.eachField.answerChoices}
      sx={{ width: p.width }}
      renderInput={(params) => (
        <TextFieldComponent
          {...params}
          placeholder={p.eachField.placeholderText ?? ""}
          error={!!_.get(p.formValidator, `error.${p.name}`)}
        />
      )}
    />
  );
};

const currentDateForDateField = (p: QuestionFieldRenderProps<number>) =>
  p.enabled ? (
    <Box
      className={`${
        p.currentAnswer?.value ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"
      } border border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
      onClick={(args) => {
        p.handleOnBlur(args);
        p.handleOnChange(args);
      }}
    >
      {p.currentAnswer?.value ? (
        convertDate(Number(p.currentAnswer?.value), "dateForTooltip")
      ) : (
        <Text className="text-xs text-slate-500">{t("Click here to set date")}</Text>
      )}
    </Box>
  ) : (
    <div
      className={`cursor-not-allowed pointer-events-none border bg-gray-100 border-slate-400 ${p.source === formSectionType.FORM ? "w-1/2" : "w-full"} h-8 flex items-center p-2`}
    >
      {p.currentAnswer?.value && convertDate(Number(p.currentAnswer?.value), "dateForTooltip")}
    </div>
  );
export const renderDateTime = (p: QuestionFieldRenderProps<number>) => {
  const min = p.eachField.minFieldRange || null;
  const max = p.eachField.maxFieldRange || null;

  // KNOWN BUG, preserved verbatim: `minDate` / `maxDate` are handed to the MUI pickers as epoch
  // numbers, but both expect a Dayjs. The pickers silently ignore the bad bound, so a rangeBased
  // date-time question is not actually range-limited in the widget (server-side validation still
  // catches it on submit). Typed as the record that is really being built, and spread through an
  // erased assertion so the spread keeps compiling exactly as it did under `any`; converting the
  // bounds would change what the widget does, which is out of scope for a typing pass.
  let extraProps: Record<string, boolean | number | null> = {};
  if (p.eachField.selectionScope === "pastDateTime") extraProps.disableFuture = true;
  if (p.eachField.selectionScope === "futureDateTime") extraProps.disablePast = !p.currentAnswer.value;
  if (p.eachField.selectionScope === "rangeBased") extraProps = { minDate: min, maxDate: max };

  if (p.eachField.selectionScope === "currentDateTime") {
    return currentDateForDateField(p);
  }

  if (p.eachField.displayAs === "dateAndTime") {
    return (
      <DateTimeComponent
        slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
        {...(extraProps as Record<string, never>)}
        disabled={!p.enabled}
        key={p.name}
        sx={{ width: p.width }}
        format="DD/MM/YYYY hh:mm A"
        error={!!_.get(p.formValidator, `error.${p.name}`)}
        defaultValue={p.eachField.defaultValue || p.currentAnswer.value || undefined}
        placeholder={p.eachField.placeholderText ?? ""}
        value={p.currentAnswer.value ?? undefined}
        onChange={p.handleOnChange}
        onAccept={p.handleOnBlur}
      />
    );
  } else {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{
            width: p.width ?? "100%",
            "&.MuiFormControl-root .MuiInputBase-root": {
              height: "2rem",
              background: "white",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--info-light-blue)",
              borderWidth: "0.0625rem",
            },
          }}
          slotProps={{ field: { clearable: true, onClear: () => p.handleOnBlur(null) } }}
          {...(extraProps as Record<string, never>)}
          disabled={!p.enabled}
          format="DD/MM/YYYY"
          // KNOWN BUG, preserved verbatim: `error` is not a prop on MUI's `DatePicker` (v6 moved it
          // to `slotProps.textField.error`), so the red validation outline has never rendered on a
          // date-ONLY question — only on the date-and-time branch above, which goes through our own
          // `DateTimeComponent` wrapper. This was invisible until now because the sibling
          // `{...extraProps}` spread was `any`, and an `any` spread switches off prop checking for
          // the whole JSX element. Suppressed rather than fixed: moving it under `slotProps` would
          // start rendering an outline that users do not currently see, which is a behaviour change
          // and out of scope for a typing pass. The suppression is self-checking — it fails the
          // build if the prop ever becomes valid.
          // @ts-expect-error -- invalid prop kept deliberately; see above.
          error={!!_.get(p.formValidator, `error.${p.name}`)}
          placeholder={p.eachField.placeholderText ?? ""}
          value={p.currentAnswer.value ? dayjs(p.currentAnswer.value) : null}
          onChange={p.handleOnChange}
          onAccept={p.handleOnBlur}
        />
      </LocalizationProvider>
    );
  }
};

const LocationTreeSelect = (p: QuestionFieldRenderProps<FieldLookUpValue[]>) => {
  const locationData = Array.isArray(p.locationData) ? p.locationData : [];

  const selectedLabel = (() => {
    const ids = Array.isArray(p.currentAnswer?.value) ? p.currentAnswer.value.map((a) => a.id ?? a) : [];
    const found = locationData.find((d) => ids.includes(d.id));
    return found?.label ?? "";
  })();

  return (
    <div>
      <TreeSelectDropDown
        disabled={!p.enabled}
        key={p.name}
        disabledParentSelection={true}
        placeholder={p.eachField?.placeholderText ?? ""}
        treeData={locationData}
        value={selectedLabel}
        onSelectionChange={(params: TreeDropDownNode) => {
          const args = { itemData: { _id: params.id, name: params.label, ...params } };
          p.handleOnChange(args);
          p.handleOnBlur(args);
        }}
      />
    </div>
  );
};

export const renderLookUp = (p: QuestionFieldRenderProps<FieldLookUpValue[]>) => {
  if (p.eachField.lookUpAnsFieldType === "Location") {
    return <LocationTreeSelect {...p} />;
  }

  if (p.eachField.lookUpAnsFieldType === "Teams") {
    return (
      <AutoComplete
        disabled={!p.enabled}
        multiple
        key={p.name}
        disableCloseOnSelect
        getOptionLabel={(opt) => opt.name}
        value={p.currentAnswer.value ?? []}
        onChange={(_, newValue) => {
          p.handleOnChange(newValue);
          p.handleOnBlur(newValue);
        }}
        id={p.name}
        options={p.teamsData}
        sx={{ width: p.width }}
        renderInput={(params) => (
          <TextFieldComponent
            {...params}
            placeholder={p.eachField.placeholderText ?? ""}
            error={!!_.get(p.formValidator, `error.${p.name}`)}
          />
        )}
      />
    );
  }

  return <></>;
};

export const renderNumber = (p: QuestionFieldRenderProps<number>) => (
  <TextFieldComponent
    defaultValue={p.eachField.defaultValue ?? p.currentAnswer.value ?? ""}
    onChange={p.handleOnChange}
    onBlur={p.handleOnBlur}
    error={!!_.get(p.formValidator, `error.${p.name}`)}
    slotProps={{
      htmlInput: {
        min: p.eachField.minFieldRange ? +p.eachField.minFieldRange : 0,
        max: p.eachField.maxFieldRange ? +p.eachField.maxFieldRange : null,
      },
    }}
    key={p.name}
    disabled={!p.enabled}
    placeholder={p.eachField.placeholderText ?? ""}
    width={p.width}
    type="number"
  />
);

export const renderUsers = (p: QuestionFieldRenderProps<FieldUserValue[]>) => (
  <div>
    <Box
      onClick={() =>
        p.enabled
          ? p.openAddUserModal({
              eachField: p.eachField,
              fieldIndex: p.fieldIndex,
              sectionIndex: p.sectionIndex,
              section: p.section,
              source: p.source,
              tableRowIndex: p.tableRowIndex,
              handleCustomFieldData: p.handleCustomFieldData,
              extraParams: p.extraParams,
              tableRowId: p.tableRowId,
            })
          : () => {}
      }
      className="bg-button_light h-8 w-14 rounded-3xl justify-center flex cursor-pointer items-center"
    >
      <IconComponent className={p.enabled ? "" : "opacity-50"} name="UserProfile" width="1.5rem" height="1.5rem" />
    </Box>
    <div className="flex flex-wrap gap-5 overflow-auto pt-5">
      {p.currentAnswer.value?.map((user) => getUserCardAndChip({ user, p }))}
    </div>
  </div>
);

export const renderFileUpload = (p: QuestionFieldRenderProps<FieldFileValue[]>) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={p.handleFileUploadClick}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    <span className="flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
      {p.currentAnswer.value?.map((data, index: number) => (
        <span
          key={`${data.name}-${index}`}
          className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
        >
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFileOpen(e, data, p.spaceId, p.projectId)}
            name="openInViewer"
            width="1rem"
            height="1rem"
          />
          <span className="w-14 h-10 items-center flex object-contain"> {getFileIcon(data.name)}</span>
          <span>{data.name}</span>
          {p.enabled && (
            <span className="w-8">
              <IconComponent
                className="cursor-pointer"
                onClick={() =>
                  p.handleRemoveUploadedFile({
                    fileData: data,
                    eachField: p.eachField,
                    sectionIndex: p.sectionIndex,
                    fieldIndex: p.fieldIndex,
                    sectionId: p.sectionId,
                    questionId: p.questionId,
                    tableRowIndex: p.tableRowIndex,
                    source: p.source,
                    handleCustomFieldData: p.handleCustomFieldData,
                    currentAnswer: p.currentAnswer,
                    extraParams: p.extraParams,
                  })
                }
                name="cross"
                width="1rem"
                height="1rem"
              />
            </span>
          )}
        </span>
      ))}
      {p.isFileUploading.map(
        (data) =>
          data.uniqueId == p.name && (
            <span
              key={data.uniqueId}
              className="w-32 h-32 border-button_light p-1 items-center justify-center rounded flex border-2"
            >
              <CircularProgress />
            </span>
          ),
      )}
    </span>
  </div>
);

export const renderImage = (p: QuestionFieldRenderProps<FieldFileValue[]>) => (
  <div className="flex h-full w-full overflow-auto flex-col gap-5">
    <span>
      <input
        accept={(p.eachField.allowedFileTypes ?? []).join(",")}
        type="file"
        id={`${p.name}`}
        onChange={(e) =>
          p.handleFileUploadChange({
            e,
            sectionId: p.sectionId,
            eachField: p.eachField,
            sectionIndex: p.sectionIndex,
            fieldIndex: p.fieldIndex,
            source: p.source,
            tableRowIndex: p.tableRowIndex,
            tableRowId: p.tableRowId,
            allowedFileTypes: p.eachField.allowedFileTypes,
            formData: p.formData,
            handleCustomFieldData: p.handleCustomFieldData,
            extraParams: p.extraParams,
          })
        }
        style={{ display: "none" }}
      />
      <ButtonComponent
        onClick={() => document.getElementById(`${p.name}`)?.click()}
        disabled={!p.enabled}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <PlusIcon color={p.enabled ? "var(--primary-text)" : "var(--system-colour-37"} />
          <span>{p.eachField.placeholderText}</span>
        </span>
      </ButtonComponent>
    </span>
    {/* Both `as number` casts are erased. `?.length` is `number | undefined` once the answer is
        typed, and `undefined > 0` is exactly the `false` this comparison already produced. */}
    {((p.currentAnswer?.value?.length as number) > 0 || (p.isFileUploading?.length as number) > 0) && (
      <span className="flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
        {p.currentAnswer.value?.map((data, index: number) => (
          <span
            key={data.awsKey}
            style={{ width: `${p.eachField?.displaySize ?? DEFAULT_IMAGE_DISPLAY_WIDTH}%` }}
            className="h-fit border-button_light p-1 rounded flex border-2"
          >
            <ImageEditor
              isEditable={p.enabled}
              imageData={`${getBackendUrl()}/fileSystemServer/getAttachmentFile/${data.awsKey}`}
              onSubmitFunction={(base64EncodedImg: string) =>
                p.saveEditedImage({
                  eachField: p.eachField,
                  fieldIndex: p.fieldIndex,
                  sectionIndex: p.sectionIndex,
                  tableRowIndex: p.tableRowIndex,
                  source: p.source,
                  imageIndex: index,
                  base64EncodedImg,
                  handleCustomFieldData: p.handleCustomFieldData,
                  currentAnswer: p.currentAnswer,
                  extraParams: p.extraParams,
                  awsKey: data.awsKey,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                })
              }
              heading={t("Edit Image")}
              onDelete={() =>
                p.handleRemoveUploadedFile({
                  eachField: p.eachField,
                  fileData: data,
                  sectionIndex: p.sectionIndex,
                  fieldIndex: p.fieldIndex,
                  sectionId: p.sectionId,
                  questionId: p.questionId,
                  source: p.source,
                  tableRowIndex: p.tableRowIndex,
                  currentAnswer: p.currentAnswer,
                  handleCustomFieldData: p.handleCustomFieldData,
                })
              }
              defaultImageData={undefined}
            />
          </span>
        ))}
        {p.isFileUploading.map(
          (data) =>
            data.uniqueId == p.name && (
              <span
                key={data.uniqueId}
                className="h-32 w-32 border-button_light p-1 items-center justify-center rounded flex border-2"
              >
                <CircularProgress />
              </span>
            ),
        )}
      </span>
    )}
  </div>
);

const getURLEditBtn = (p: QuestionFieldRenderProps<FieldUrlValue>) => {
  return (
    <div>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() => {
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          });
        }}
      >
        <IconComponent
          className={`h-5 ${p.enabled ? "" : "opacity-50"}`}
          name="editProfile"
          width="1.25rem"
          height="1.25rem"
        />
      </ButtonComponent>
    </div>
  );
};
export const renderURL = (p: QuestionFieldRenderProps<FieldUrlValue>) => {
  if (p.currentAnswer?.value?.displayText && p.currentAnswer?.value?.URL) {
    if (p.eachField.displayAs == "button") {
      return (
        <div className="flex items-center gap-3">
          <ButtonComponent onClick={p.handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{_.get(p.currentAnswer, "value.displayText", "")}</Text>
          </ButtonComponent>
          {getURLEditBtn(p)}
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-3">
          <Box
            onClick={p.handleUrlFieldClick}
            className="text-gradient_color cursor-pointer underline underline-offset-2"
          >
            {_.get(p.currentAnswer, "value.displayText", "")}
          </Box>
          {getURLEditBtn(p)}
        </div>
      );
    }
  }
  return (
    <span>
      <ButtonComponent
        disabled={!p.enabled}
        onClick={() =>
          p.handleURLModalState({
            state: true,
            sectionIndex: p.sectionIndex,
            eachField: p.eachField,
            tableRowIndex: p.tableRowIndex,
            answer: p.currentAnswer,
            fieldIndex: p.fieldIndex,
            sectionId: p.sectionId,
            source: p.source,
            tableRowId: p.tableRowId,
          })
        }
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      >
        <span className="flex gap-1 items-center">
          <IconComponent name="URL" width="1.5rem" height="1.5rem" className={`h-5 ${p.enabled ? "" : "opacity-50"}`} />
          <Text>{t("Enter URL")}</Text>
        </span>
      </ButtonComponent>
    </span>
  );
};

export const renderGeoLocation = (p: QuestionFieldRenderProps<FieldGeoValue>) => {
  const distance = getDistanceInMeters({
    pos1: p.currentAnswer.value?.current,
    pos2: p.currentAnswer.value?.selected,
  });
  return p.currentAnswer.value ? (
    <div className="flex-col flex gap-5">
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!p.currentAnswer.value?.current}
          zoomLevel={p.currentAnswer.value?.mapZoomLevel}
          defaultCenter={p.currentAnswer.value?.current}
          additionalMarker={p.currentAnswer.value?.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {p.eachField?.captureLiveLocation && !p.currentAnswer.value?.current && getNoPermissionWarning()}
      </div>
      <div>
        <ButtonComponent disabled={!p.enabled} onClick={p.handleRemoveLocation}>
          <span className="flex gap-1 items-center">
            <IconComponent className={p.enabled ? "" : "opacity-50"} name="delete" width="1.25rem" height="1.25rem" />
            <Text>{t("Remove")}</Text>
          </span>
        </ButtonComponent>
      </div>
    </div>
  ) : (
    <ButtonComponent
      onClick={() =>
        p.handleGeoLocationModalState({
          sectionIndex: p.sectionIndex,
          eachField: p.eachField,
          tableRowIndex: p.tableRowIndex,
          answer: p.currentAnswer,
          fieldIndex: p.fieldIndex,
          sectionId: p.sectionId,
          source: p.source,
          tableRowId: p.tableRowId,
          state: true,
        })
      }
      disabled={!p.enabled}
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
    >
      <span className="flex gap-1 items-center">
        <IconComponent className={p.enabled ? "" : "opacity-50"} name="add" width="1rem" height="1rem" />
        <span>{p.eachField.placeholderText}</span>
      </span>
    </ButtonComponent>
  );
};

const renderFormulaField = (p: QuestionFieldRenderProps) => {
  const calculatedValue = p.formulaValue ?? null;
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={calculatedValue ?? ""} key={p.name} disabled={true} type="number" />
      {calculatedValue === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {getCalculatedFieldHintText()}
        </div>
      )}
    </div>
  );
};

const renderConcatenateField = (p: QuestionFieldRenderProps) => {
  const previewNodes = p.concatenatedValue ?? null;
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={previewNodes ?? ""} multiline key={p.name} disabled={true} type="text" />
      {previewNodes === null && (
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <span className="flex items-center justify-center">
            <IconComponent className="h-5" name="details" width="1.5rem" height="1.5rem" />
          </span>
          {getCalculatedFieldHintText()}
        </div>
      )}
    </div>
  );
};
const renderAutoNumberField = (p: QuestionFieldRenderProps<string | number>) => {
  return (
    <div className="flex flex-col gap-2" style={{ width: p.width }}>
      <TextFieldComponent value={p.currentAnswer?.value ?? ""} key={p.name} disabled={true} type="number" />
    </div>
  );
};
/**
 * fieldType → renderer. The value type is `never` so that every renderer, each of which declares
 * the concrete answer shape its own fieldType stores, is assignable here (parameters are
 * contravariant, and `never` is assignable to every value shape). The single caller casts its
 * props object at the invocation; see questionFields.tsx.
 */
export const fieldRenderers: Record<string, (p: QuestionFieldRenderProps<never>) => ReactNode> = {
  "single-line": renderSingleLine,
  "multi-line": renderMultiLine,
  "single-select": renderSingleSelect,
  "multi-select": renderMultiSelect,
  "date-time": renderDateTime,
  "look-up": renderLookUp,
  number: renderNumber,
  users: renderUsers,
  fileUpload: renderFileUpload,
  image: renderImage,
  url: renderURL,
  geoLocation: renderGeoLocation,
  formula: renderFormulaField,
  concatenate: renderConcatenateField,
  autoNumber: renderAutoNumberField,
};
export const handleFileOpen = (
  e: React.MouseEvent,
  data: FieldFileValue,
  spaceId: string | undefined,
  projectId: string | undefined,
) => {
  e.stopPropagation();
  const { awsKey, fileVersion } = data;
  // `getViewerUrl` early-returns `undefined` when it has no `projectId`, so this is genuinely
  // `string | undefined`; the `if (url)` guard at the bottom already handled that case.
  let url: string | undefined = "";
  if (awsKey) {
    url = `${getBackendUrl()}/fileSystemServer/getAttachmentFile/${awsKey}`;
  } else if (fileVersion) {
    url = getViewerUrl({
      fileVersionId: fileVersion,
      spaceId,
      projectId,
    });
  }
  if (url) {
    window.open(url, "_blank");
  }
};

export const getFieldAns = (
  props: FieldAnswerViewProps,
  currentAnswer: FieldAnswer<unknown>,
  handleUrlFieldClick: () => void,
  projectId?: string,
) => {
  const { eachField, spaceId, section } = props;
  const { fieldType, displayAs, captureLiveLocation, displaySize = DEFAULT_IMAGE_DISPLAY_WIDTH } = eachField;

  if (["single-select", "number", "single-line", "multi-line", "autoNumber"].includes(fieldType)) {
    return <div className="w-4/5 flex flex-wrap">{currentAnswer.value as string}</div>;
  } else if (fieldType == "multi-select") {
    return (currentAnswer.value as string[] | undefined)?.join(",");
  } else if (fieldType == "date-time") {
    if (displayAs === "dateAndTime") {
      return currentAnswer.value ? convertDate(Number(currentAnswer.value), "dateForTooltip") : "";
    } else {
      return currentAnswer.value ? convertDate(Number(currentAnswer.value), "dateForProfile") : "";
    }
  } else if (fieldType == "look-up") {
    const ansDisplay = (currentAnswer.value as FieldLookUpValue[] | undefined)?.map((ans) => ans.name);
    return ansDisplay?.length ? ansDisplay?.join(",") : "";
  } else if (fieldType == "users") {
    return (
      <div className="flex flex-wrap gap-5 pt-5">
        {(currentAnswer.value as FieldUserValue[] | undefined)?.map((user) =>
          getUserCardAndChip({ user, p: { ...props, enabled: false } }),
        )}
      </div>
    );
  } else if (fieldType == "fileUpload") {
    return (
      <span className="form-answer-file-list flex flex-col pe-3 overflow-auto gap-4 w-full h-full">
        {(currentAnswer.value as FieldFileValue[] | undefined)?.map((data, index: number) => (
          <span
            key={`${data.name}-${index}`}
            className="flex gap-3 border-solid border-2 border-button_light w-fit pe-2 rounded-lg ps-2 items-center"
          >
            <IconComponent
              onClick={(e: React.MouseEvent) => handleFileOpen(e, data, spaceId, projectId)}
              name="openInViewer"
              width="1.25rem"
              height="1.25rem"
            />
            <span className="w-14 h-10 flex items-center object-contain">{getFileIcon(data.name)}</span>
            <span>{data.name}</span>
          </span>
        ))}
      </span>
    );
  } else if (fieldType == "image") {
    let answerValue = currentAnswer.value as FieldFileValue[] | undefined;
    return (
      <div className="form-answer-image-wrap flex flex-wrap flex-row pe-3 overflow-auto gap-4 w-full h-full">
        {answerValue?.map((data) => (
          <span
            key={data.awsKey}
            style={{ width: `${displaySize}%` }}
            className="h-fit border-button_light p-1 rounded flex border-2"
          >
            <img
              src={`${getBackendUrl()}/fileSystemServer/getAttachmentFile/${data.awsKey}`}
              alt="logo"
              width="100%"
              className="form-answer-image"
            />
          </span>
        ))}
      </div>
    );
  } else if (fieldType == "url") {
    if (!_.get(currentAnswer, "value.displayText", "")) {
      return "";
    }
    if (displayAs == "button") {
      return (
        <div>
          <ButtonComponent onClick={handleUrlFieldClick} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text sx={{ maxWidth: "12.5rem" }}>{_.get(currentAnswer, "value.displayText", "")}</Text>
          </ButtonComponent>
        </div>
      );
    } else {
      return (
        <Link
          href={getUrlFromField(_.get(currentAnswer, "value.URL", ""))}
          target="_blank"
          underline="always"
          className="cursor-pointer"
        >
          {_.get(currentAnswer, "value.displayText", "")}
        </Link>
      );
    }
  } else if (fieldType == "geoLocation") {
    // The `as FieldGeoValue | undefined` casts are erased, so the emitted JS is byte-identical to
    // HEAD. Deliberately NOT hoisted into a `const geoValue = …`: a hoisted local IS emitted, which
    // changes `currentAnswer.value` from two reads to one and shows up as a real diff in the
    // emitted-JS comparison. Repeating the cast is the type-only way to express this.
    const distance = getDistanceInMeters({
      pos1: (currentAnswer.value as FieldGeoValue | undefined)?.current,
      pos2: (currentAnswer.value as FieldGeoValue | undefined)?.selected,
    });
    return currentAnswer.value ? (
      <div className="flex h-80 w-120 border flex-col gap-5 border-button_light p-3 rounded-lg">
        <GoogleMap
          allowManualSelection={false}
          allowPanning={false}
          autoFetchLiveLocation={false}
          showLiveLocation={!!(currentAnswer.value as FieldGeoValue | undefined)?.current}
          zoomLevel={(currentAnswer.value as FieldGeoValue | undefined)?.mapZoomLevel}
          defaultCenter={(currentAnswer.value as FieldGeoValue | undefined)?.current}
          additionalMarker={(currentAnswer.value as FieldGeoValue | undefined)?.selected}
          allowSearch={false}
          getLocationCallBack={undefined}
        />
        {distance > 500 && getOverDistanceWarning()}
        {captureLiveLocation &&
          !(currentAnswer.value as FieldGeoValue | undefined)?.current &&
          getNoPermissionWarning()}
      </div>
    ) : (
      ""
    );
  } else if (fieldType == "formula") {
    return getFormulaFieldValue({
      eachField,
      section,
      tableRowIndex: props.tableRowIndex,
      formData: props.formData,
    });
  } else if (fieldType == "concatenate") {
    return (
      getConcatenateFieldValue({
        eachField,
        section,
        tableRowIndex: props.tableRowIndex,
        formData: props.formData,
      }) ?? ""
    );
  }
  return "";
};
